import { find, head } from "lodash";
import { Locale } from "@linkfluence/models/locale";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import {
  DatavizHorizontalBarChart,
  updateDatavizOptions,
} from "@radarly/models/dataviz";

function isMainUnitTotal(mainUnit: DatavizHorizontalBarChart["mainUnit"]) {
  return mainUnit !== "percentage";
}

function getSecondaryUnitOptions(
  locale: Locale,
  mainUnit: DatavizHorizontalBarChart["mainUnit"]
) {
  if (!isMainUnitTotal(mainUnit)) {
    return [
      {
        value: "total",
        label: tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::YES"),
      },
      {
        value: "none",
        label: tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::NO"),
      },
    ];
  }
  return [
    {
      value: "percentage",
      label: tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::YES"),
    },
    {
      value: "none",
      label: tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::NO"),
    },
  ];
}

function getSecondaryUnitOption(
  dataviz: DatavizHorizontalBarChart,
  locale: Locale,
  mainUnit: DatavizHorizontalBarChart["mainUnit"]
) {
  const options = getSecondaryUnitOptions(locale, mainUnit);
  const defaultOption = head(options);
  const currentOption = find(options, { value: dataviz.secondaryUnit });
  if (!isMainUnitTotal(mainUnit)) {
    return dataviz.secondaryUnit === "percentage"
      ? defaultOption
      : currentOption || defaultOption;
  }
  return currentOption || defaultOption;
}

function getLabel(
  locale: Locale,
  mainUnit: DatavizHorizontalBarChart["mainUnit"]
) {
  if (!isMainUnitTotal(mainUnit)) {
    return tr(
      locale,
      "CUSTOM_PAGES::EDITOR::DATAVIZ::HORIZONTAL_BAR_CHART::DISPLAY_TOTAL_SECONDARY_VALUE"
    );
  }
  return tr(
    locale,
    "CUSTOM_PAGES::EDITOR::DATAVIZ::HORIZONTAL_BAR_CHART::DISPLAY_PERCENTAGE_SECONDARY_VALUE"
  );
}

export function useSecondaryUnit(
  dataviz: DatavizHorizontalBarChart,
  setDataviz: (dataviz: DatavizHorizontalBarChart) => void
) {
  const locale = useCurrentUserLocale();
  const label = getLabel(locale, dataviz.mainUnit);
  const options = getSecondaryUnitOptions(locale, dataviz.mainUnit);
  const option = getSecondaryUnitOption(dataviz, locale, dataviz.mainUnit);
  const onChange = (id: string) => {
    setDataviz(updateDatavizOptions(dataviz, "secondaryUnit", id));
  };
  return {
    label,
    options,
    option,
    onChange,
    isDisabled: dataviz.secondaryUnit === "evolution",
  };
}
